import React, { useState } from 'react';
import { Box, Typography, Button, CircularProgress, Dialog,
         DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Explore } from '@mui/icons-material';
import { useTourStatus } from '../../hooks/useTourStatus';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Persistent top banner shown on all pages while tour data is active.
 * Offers "Reset tour data" and "Start fresh" (exit) actions.
 */
const TourBanner = () => {
  const { active, loading, exit, reset } = useTourStatus();
  const { t } = useLanguage();
  const [busy, setBusy] = useState(false);
  const [confirmExit, setConfirmExit] = useState(false);

  // The sample-data banner is for a farmer exploring a trial account. During
  // the presentation it is noise across the top of every screen -- and worse,
  // it names the data as a sample at the exact moment the pitch is that this
  // is the real product on a real orchard. Hidden for the length of a run.
  const inShowcase = typeof document !== 'undefined'
    && document.body.getAttribute('data-showcase') === 'on';
  if (loading || !active || inShowcase) return null;

  const handleReset = async () => {
    setBusy(true);
    try { await reset(); } finally { setBusy(false); }
  };

  const handleExit = async () => {
    setBusy(true);
    setConfirmExit(false);
    try { await exit(); window.location.href = '/'; } finally { setBusy(false); }
  };

  return (
    <>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        bgcolor: '#0f172a', color: '#fff',
        px: 2, py: 0.75, minHeight: 40, flexShrink: 0,
        borderBottom: '1px solid rgba(16,185,129,0.3)',
        zIndex: 1200,
      }}>
        <Explore sx={{ fontSize: 16, color: '#10b981', flexShrink: 0 }} />
        <Typography variant="caption" sx={{ fontSize: '0.8rem', flex: 1, color: 'rgba(255,255,255,0.85)' }}>
          {t('tourBannerLabel') || 'Tour data — explore freely'}
        </Typography>
        {busy ? (
          <CircularProgress size={14} sx={{ color: '#10b981' }} />
        ) : (
          <>
            <Button size="small" onClick={handleReset}
              sx={{ color: '#10b981', fontSize: '0.75rem', fontWeight: 600, py: 0.25, px: 1, minHeight: 28 }}>
              {t('resetTourData') || 'Reset tour data'}
            </Button>
            {/* The destructive one should look destructive. It was the
                greyer, quieter of the two buttons while being the one that
                deletes every demo field, zone, schedule and alert. */}
            <Button size="small" onClick={() => setConfirmExit(true)}
              sx={{ color: '#fca5a5', fontSize: '0.75rem', py: 0.25, px: 1, minHeight: 28 }}>
              {t('startFresh') || 'Start fresh'}
            </Button>
          </>
        )}
      </Box>

      <Dialog open={confirmExit} onClose={() => setConfirmExit(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t('exitTourTitle') || 'Delete demo data?'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t('exitTourBody') || 'All demo fields, zones, schedules and alerts will be deleted. Your account will be blank and ready for real data.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmExit(false)}>{t('cancel') || 'Cancel'}</Button>
          <Button variant="contained" color="error" onClick={handleExit}>
            {t('deleteAndContinue') || 'Delete & continue'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TourBanner;
